/**
 * Wrapper around the external library we use for working with times, dates,
 * which is "dayjs", as well as a collection of utility functions directly
 * dealing with timestamps.
 */
// this file is the only one from which dayjs imports are allowed
// eslint-disable-next-line no-restricted-imports
import dayjs from 'dayjs'
// eslint-disable-next-line no-restricted-imports
import advancedFormatPlugin from 'dayjs/plugin/advancedFormat'
// eslint-disable-next-line no-restricted-imports
import durationPlugin from 'dayjs/plugin/duration'
// eslint-disable-next-line no-restricted-imports
import relativeTimePlugin from 'dayjs/plugin/relativeTime'
// eslint-disable-next-line no-restricted-imports
import updateLocalePlugin from 'dayjs/plugin/updateLocale'
// eslint-disable-next-line no-restricted-imports
import utcPlugin from 'dayjs/plugin/utc'
// eslint-disable-next-line no-restricted-imports
import timezone from 'dayjs/plugin/timezone'

import { addZeros } from './formatter'

export const TimeRange = {
  TODAY: 'today',
  YESTERDAY: 'yesterday',
  LAST_7_DAYS: 'last_7_days',
  LAST_14_DAYS: 'last_14_days',
  LAST_30_DAYS: 'last_30_days',
  THIS_MONTH: 'this_month',
  YEAR_TO_DATE: 'year_to_date',
  ALL_TIME: 'all_time',
} as const

export const Duration = {
  SECOND: 's',
  MINUTE: 'm',
  HOUR: 'h',
  DAY: 'd',
} as const
export type DurationType = (typeof Duration)[keyof typeof Duration]

dayjs.extend(advancedFormatPlugin) // 'Do' in format, etc
dayjs.extend(durationPlugin) // Using .duration()
dayjs.extend(relativeTimePlugin) // .fromNow()
dayjs.extend(updateLocalePlugin) // .updateLocale()
dayjs.extend(utcPlugin) // .utc()
dayjs.extend(timezone) // .tz()

/**
 * Formats a date specified as ISO timestamp into a 'time ago' string
 */
export const getAgoString = (date: string): string => dayjs.utc(date).fromNow()

/**
 * Formats a duration amount, specifed in seconds, into a HH:MM:SS string.
 *
 * The HH part of the string is discarded if duration is less than an hour.
 */
export const formatDurationAsTimer = (
  durationInSeconds: number,
  separator: string = ':',
): string => {
  const seconds = durationInSeconds % 60
  const minutes = Math.floor((durationInSeconds % 3600) / 60)
  const hours = Math.floor(durationInSeconds / 3600)
  return (hours ? [hours, minutes, seconds] : [minutes, seconds])
    .map((p) => addZeros(p, 2))
    .join(separator)
}

/**
 * Formats a duration amount, specified in seconds into a Xd Xh Xm string.
 *
 * The d and h parts are discarded if duration is less than a day, or an hour, respectively.
 *
 * NOTE: this the legacy version still used by ReportsLegacy. As we're updating its logic we prefer
 * to duplicated this code and later remove it when sunsetting Reports V2
 */
export const legacyFormatDurationAsSpan = (
  durationInSeconds: number,
  maxUnit: 'd' | 'h' = 'd',
): string => {
  const duration = dayjs.duration(durationInSeconds, 'seconds')
  const parts = []

  if (maxUnit === 'd') {
    // duration.get('d') will not include months
    // duration.asDays() returns a float
    if (durationInSeconds >= 24 * 3600) {
      parts.push(`${Math.floor(duration.asDays())}d`)
    }
    if (durationInSeconds >= 3600) {
      parts.push(`${duration.get('h')}h`)
    }
  } else {
    if (durationInSeconds >= 3600) {
      parts.push(`${Math.floor(duration.asHours())}h`)
    }
  }

  parts.push(`${duration.get('m')}m`)

  return parts.join(' ')
}

/**
 * Formats a duration amount, specified in seconds into a Xd Xh Xm Xs string.
 *
 * The output includes only non-zero units, constrained by minUnit and maxPrecision.
 * If the duration is zero, it returns '0s' by default.
 */
export const formatDurationAsSpan = (
  durationInSeconds: number,
  options: {
    minUnit?: DurationType
    maxPrecision?: number
  } = {},
): string => {
  const { minUnit = Duration.SECOND, maxPrecision = 3 } = options

  if (durationInSeconds === 0) {
    return `0${Duration.SECOND}`
  }

  const duration = dayjs.duration(durationInSeconds, 'seconds')

  const units = [
    { type: Duration.DAY, value: Math.floor(duration.asDays()) },
    { type: Duration.HOUR, value: duration.get(Duration.HOUR) },
    { type: Duration.MINUTE, value: duration.get(Duration.MINUTE) },
    { type: Duration.SECOND, value: duration.get(Duration.SECOND) },
  ]

  // Get units starting from DAY down to minUnit
  const minUnitIndex = units.map(({ type }) => type).indexOf(minUnit)
  const relevantUnits = units.slice(0, minUnitIndex + 1)

  const parts = []

  for (const unit of relevantUnits) {
    if (unit.value > 0) {
      parts.push(`${unit.value}${unit.type}`)
    }
    if (parts.length >= maxPrecision) {
      break
    }
  }

  // If no non-zero units were found, return '0s'
  if (parts.length === 0) {
    return `0${Duration.SECOND}`
  }

  return parts.join(' ')
}

export const dateFromUtcISO = (iso: string): Date => dayjs.utc(iso).toDate()

/**
 * Formats a date string in ISO format, not assuming timezone, to the specified format
 */
export const formatDate = (value: string, format: string): string => dayjs(value).format(format)

/**
 * Takes an iso value of a date, assuming it's in UTC,
 * transforms it to the local timezone and then reformats it to specified format
 */
export const formatUTCToLocalDate = (value: string, format: string): string =>
  dayjs(dateFromUtcISO(value)).format(format)

/**
 * Takes an iso value of a date, assuming it's in UTC,
 * and then reformats it to specified format
 */
export const formatUTCDate = (value: string, format: string): string =>
  dayjs.utc(value).format(format)

/**
 * Formats a date in unix timestamp format to the specified format
 */
export const formatUnixDate = (value: number, format: string): string =>
  dayjs.unix(value).format(format)

const utcNow = (): dayjs.Dayjs => dayjs.utc()

export const utcNowISO = (): string => utcNow().toISOString()

export const startOfYear = (format: string): string => utcNow().startOf('year').format(format)

/**
 * Returns ISO string representing the start of the day for the specified ISO string
 */
export const startOfDay = (isoStamp: string = utcNowISO()): string =>
  dayjs.utc(isoStamp).startOf('day').toISOString()

export const startOfHour = (isoStamp: string = utcNowISO()): string =>
  dayjs.utc(isoStamp).startOf('day').toISOString()

export const startOfMinute = (): string => utcNow().startOf('minute').toISOString()

/**
 * Returns current unix timestamp, in milliseconds
 */
export const unixNowMs = (): number => Date.now()

/**
 * Returns current unix timestamp, in seconds
 */
export const unixNowSeconds = (): number => Math.floor(unixNowMs() / 1000)

const dateFromUnixNowMs = (unixNowMs: number): Date => new Date(unixNowMs)

/**
 * Takes unix seconds timestamp and converts it to ISO string
 */
export const unixToIso = (unixNowMs: number): string => dateFromUnixNowMs(unixNowMs).toISOString()

export const unixSecondsFromIso = (iso: string): number =>
  Math.floor(dateFromUtcISO(iso).getTime() / 1000)

export const uniformListOfTimestamps = (
  fromDate: string,
  step: 'hour' | 'day',
  format: string,
): string[] => {
  let from = dayjs.utc(fromDate)
  const now = utcNow()

  const diff = now.diff(from, step)
  const dates: string[] = []

  for (let i = 0; i <= diff; i++) {
    dates.push(from.format(format))
    from = from.add(1, step)
  }

  return dates
}

export const addTime = (isoStamp: string, amount: number, unit: 'day' | 'month' | 'week'): string =>
  dayjs.utc(isoStamp).add(amount, unit).toISOString()

export const timeRangeISOString = (value: string): { start: string; end: string } => {
  const now = dayjs()

  if (value === TimeRange.TODAY) {
    return {
      start: now.startOf('day').toISOString(),
      end: now.endOf('day').toISOString(),
    }
  }
  if (value === TimeRange.YESTERDAY) {
    return {
      start: now.subtract(1, 'day').startOf('day').toISOString(),
      end: now.subtract(1, 'day').endOf('day').toISOString(),
    }
  }
  if (value === TimeRange.LAST_7_DAYS) {
    return {
      start: now.subtract(7, 'day').startOf('day').toISOString(),
      end: now.endOf('day').toISOString(),
    }
  }
  if (value === TimeRange.LAST_14_DAYS) {
    return {
      start: now.subtract(14, 'day').startOf('day').toISOString(),
      end: now.endOf('day').toISOString(),
    }
  }
  if (value === TimeRange.LAST_30_DAYS) {
    return {
      start: now.subtract(30, 'day').startOf('day').toISOString(),
      end: now.endOf('day').toISOString(),
    }
  }
  if (value === TimeRange.THIS_MONTH) {
    return {
      start: now.startOf('month').toISOString(),
      end: now.endOf('day').toISOString(),
    }
  }
  if (value === TimeRange.YEAR_TO_DATE) {
    return {
      start: now.startOf('year').toISOString(),
      end: now.endOf('day').toISOString(),
    }
  }

  // all time as the darwin platform was release on 2019 december
  const DARWIN_RELEASE_DATE = '2019-12-01'
  return {
    start: dayjs(DARWIN_RELEASE_DATE).startOf('day').toISOString(),
    end: now.endOf('day').toISOString(),
  }
}

/**
 * Returns date in ISO format converted to UTC from the specified timezone.
 * Used in DatePicker to convert the selected date to UTC before saving it.
 * NOTE: Keep in mind that the Datepicker does an extra conversion to the local timezone
 * before converting to specified timezone.
 * @param date
 * @param tz
 * @returns ISO string
 */
export const convertFromTzToUTC = (date: dayjs.ConfigType, tz: string): string =>
  dayjs.tz(date, 'YYYY-MM-DDTHH:mm:ss', tz).tz(dayjs.tz.guess()).format('YYYY-MM-DDTHH:mm:ss[Z]')

/**
 * Returns date in ISO format converted to the specified timezone from UTC
 * @param timezone The timezone to get the offset for
 * @returns The UTC offset in the format UTC±HH:MM
 */
export const getTzPrefixWithOffset = (timezone: string): { prefix: string; offset: number } => {
  const offsetMinutes = dayjs().tz(timezone).utcOffset()
  const sign = offsetMinutes >= 0 ? '+' : '-'
  const hours = Math.floor(Math.abs(offsetMinutes) / 60)
    .toString()
    .padStart(2, '0')
  const minutes = (Math.abs(offsetMinutes) % 60).toString().padStart(2, '0')

  return { prefix: `UTC${sign}${hours}:${minutes}`, offset: offsetMinutes }
}

/**
 * Formats an ISO date string into a month and year format like "Jan 2021"
 * @param isoString - The ISO date string to format
 * @returns The formatted date string
 */
export const formatDateToMonthYear = (date: dayjs.ConfigType): string =>
  dayjs.utc(date).format('MMM YYYY')
